import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { appEvents } from "@/legacy/react/events";
import { legacyAction } from "./legacySlice";
import type { RootState } from "./store";
import { shellApi } from "@/features/shell/api";
import { sessionExpired, signedOut } from "@/features/session/slice";
import { baseApi } from "@/shared/api/baseApi";
import {
  bootstrapCompleted,
  configurationDiscarded,
  configurationSaved,
  navigationDelegated,
  shopChanged,
  shopSwitchDelegated,
} from "@/features/shell/actions";
import { startEditing } from "@/features/configuration/slice";
import { initialShop, isEmptyConfiguration } from "@/features/shops/lib";
import { normalizeSelectedPlan } from "@/features/billing/lib/plan";
import { selectCurrentConfiguration } from "@/features/shell/legacySelectors";
import type { Shop } from "@/shared/types/shop";
import type { BillingInfo, LanguageOption, SelectedPlan, UserInfo } from "@/shared/types/user";

const setLanguages = legacyAction<LanguageOption[]>("setLanguages");
const setBillingInfo = legacyAction<BillingInfo>("setBillingInfo");
const setUserInfo = legacyAction<UserInfo>("setUserInfo");
const setSelectedPlan = legacyAction<SelectedPlan>("setSelectedPlan");
const setNewNotificationCounter = legacyAction<number>("setNewNotificationCounter");
const setWebsites = legacyAction<Shop[]>("setWebsites");
const setFeatureFlags = legacyAction<Record<string, boolean>>("setFeatureFlags");
const setShowDiscardChangesModal = legacyAction<boolean>("setShowDiscardChangesModal");
const setPendingRouting = legacyAction<string>("setPendingRouting");

/**
 * Ponte tra lo shell nuovo e il mondo legacy (bus eventi + slice): unico punto del codice nuovo
 * che conosce `appEvents`. Le viste legacy continuano a ricevere gli stessi eventi di prima.
 * Sparisce con l'ultima vista legacy (fase 8).
 */
// Non generico: RootState/AppDispatch vengono da store.ts, che importa questo file come valore.
// Renderlo generico su quei tipi chiuderebbe il ciclo di import sui tipi (vedi cast su getState sotto).
export const legacyBridgeMiddleware = createListenerMiddleware();
const on = legacyBridgeMiddleware.startListening;

on({
  actionCreator: bootstrapCompleted,
  effect: ({ payload }, api) => {
    // Stesso ordine di AuthBootstrap in App.jsx.
    api.dispatch(setLanguages(payload.languages));
    api.dispatch(setBillingInfo(payload.billingInfo));
    api.dispatch(setFeatureFlags(payload.featureFlags));
    api.dispatch(setUserInfo(payload.user));
    api.dispatch(setSelectedPlan(normalizeSelectedPlan(payload.plan)));
    api.dispatch(setNewNotificationCounter(payload.unreadNotifications));
    api.dispatch(setWebsites(payload.shops));
    appEvents.$emit("check-indexing");
    appEvents.$emit("check-sync");
    if (isEmptyConfiguration(selectCurrentConfiguration(api.getState() as RootState))) {
      const initial = initialShop(payload.shops);
      if (initial) api.dispatch(startEditing(initial));
    }
  },
});

on({
  actionCreator: shopChanged,
  effect: () => {
    appEvents.$emit("changed-website");
  },
});
on({
  actionCreator: shopSwitchDelegated,
  effect: ({ payload }) => {
    appEvents.$emit("before-change-website", payload);
  },
});
on({
  actionCreator: navigationDelegated,
  effect: ({ payload }, api) => {
    api.dispatch(setShowDiscardChangesModal(true));
    api.dispatch(setPendingRouting(payload));
  },
});
on({
  matcher: shellApi.endpoints.getNotificationsCount.matchFulfilled,
  effect: ({ payload }, api) => {
    api.dispatch(setNewNotificationCounter(payload.unread));
  },
});
on({
  actionCreator: configurationSaved,
  effect: () => {
    // SaveChanges.saveChanges: setTimeout(() => this.$root.$emit("check-indexing"), 1000)
    window.setTimeout(() => appEvents.$emit("check-indexing"), 1000);
  },
});
on({
  actionCreator: configurationDiscarded,
  effect: () => {
    appEvents.$emit("discard-changes");
  },
});
// Ogni rilettura degli shop (invalidazione del tag Shop dopo salvataggio/attivazione) aggiorna lo slice
// legacy, come facevano le viste con `axios.get(SHOPS).then(setWebsites)`.
on({
  matcher: shellApi.endpoints.getShops.matchFulfilled,
  effect: ({ payload }, api) => {
    api.dispatch(setWebsites(payload));
  },
});
// Ogni lettura del piano (bootstrap, polling, riletture dopo una mutation) e del cliente aggiorna lo slice
// legacy: la verita' di piano e dati di fatturazione resta li' finche' Header e le viste legacy lo leggono.
on({
  matcher: shellApi.endpoints.getPlan.matchFulfilled,
  effect: ({ payload }, api) => {
    api.dispatch(setSelectedPlan(normalizeSelectedPlan(payload)));
  },
});
on({
  matcher: shellApi.endpoints.getCustomer.matchFulfilled,
  effect: ({ payload }, api) => {
    api.dispatch(setBillingInfo(payload));
  },
});

// Logout e sessione scaduta: la cache RTK Query appartiene all'account uscito. Senza svuotarla, un nuovo
// login entro la vita della cache servirebbe users/me, customers/me, piano, shop e notifiche del precedente
// e bootstrapCompleted li copierebbe nello slice legacy.
// Il reset aspetta un giro dell'event loop: `sessionExpired` parte da dentro la baseQuery della richiesta
// 401/403, e azzerare subito farebbe perdere a chi l'ha lanciata il proprio risultato (`result.error`).
// Le richieste ancora in volo che finiscono dopo il reset vengono scartate da RTK Query (substate assente).
on({
  matcher: isAnyOf(signedOut, sessionExpired),
  effect: async (_action, api) => {
    await api.delay(0);
    api.dispatch(baseApi.util.resetApiState());
  },
});
